import { createPlantAsset } from '../../Services/serviceURL';
import type { SensorUomResponse } from '../../Services/serviceURL';
import {
  AssetDetails,
  AssetType,
  PlantData,
  UIParameter,
  buildMissingFieldsErrorMessage,
  mapToUIParameters,
  validateCustomProbeUoms,
  validateSimNumber,
} from './createPlantDetailsModel';

export interface MappedAssetParameter {
  tag: string;
  sensorName: string;
  position: string;
  displayName: string;
  unit: string;
  unitDescription: string;
}

export interface PlantAssetSaveFormState {
  selectedAssetId: string | null;
  selectedAsset: string;
  assetName: string;
  assetStatus: string;
  gatewayDeviceType: string;
  gatewayDeviceId: string;
  simNumber: string;
  controllerType: string;
  controllerDeviceId: string;
  parameters: UIParameter[];
}

export type PlantAssetSaveValidationFailure = {
  message: string;
  simNumberError?: string;
  gatewaySerialError?: string;
};

export type PlantAssetSaveValidationSuccess = {
  plantId: string;
  selectedAssetType: AssetType;
  mappedParameters: MappedAssetParameter[];
};

export function collectMissingAssetFields(form: PlantAssetSaveFormState): string[] {
  const missingFields: string[] = [];
  if (!form.assetName.trim()) {
    missingFields.push('Asset Name');
  }
  if (!form.gatewayDeviceId.trim()) {
    missingFields.push('Gateway Serial Number');
  }
  if (!form.simNumber.trim()) {
    missingFields.push('SIM Number');
  }
  if (!form.controllerDeviceId.trim()) {
    missingFields.push('Controller Serial Number');
  }
  return missingFields;
}

export function isDuplicateAssetName(
  assetHierarchy: AssetDetails[],
  assetName: string,
  selectedAssetId: string | null
): boolean {
  return assetHierarchy.some(
    (asset) =>
      asset.name.trim().toLowerCase() === assetName.trim().toLowerCase() &&
      asset.id !== selectedAssetId
  );
}

export function getStoredPlantId(): string | null {
  return localStorage.getItem('currentplantguid') || localStorage.getItem('currentPlantId');
}

export function mapParametersForPlantAssetSave(parameters: UIParameter[]): MappedAssetParameter[] {
  if (!Array.isArray(parameters)) {
    return [];
  }

  return parameters.map((param) => {
    const sensorName =
      param.isUserConfigurable === true && param.selectedSensorType
        ? param.selectedSensorType
        : param.sensor;

    let unitDescription = '';
    if (param.units) {
      if (param.isUserConfigurable === true && param.unitOptions.length > 0) {
        const unitOption = param.unitOptions.find((opt) => opt.code === param.units);
        unitDescription = unitOption?.description || '';
      } else if (param.originalUnits.length > 0) {
        const unitOption = param.originalUnits.find((opt) => opt.code === param.units);
        unitDescription = unitOption?.description || '';
      }
    }

    return {
      tag: param.tag,
      sensorName,
      position: param.position,
      displayName: param.businessFriendlyName,
      unit: param.units,
      unitDescription,
    };
  });
}

export function validatePlantAssetBeforeSave(input: {
  form: PlantAssetSaveFormState;
  assetHierarchy: AssetDetails[];
  assetTypes: AssetType[];
  validUoms: SensorUomResponse[];
  getGatewaySerialValidationMessage: (serial: string) => string;
}):
  | { ok: true; data: PlantAssetSaveValidationSuccess }
  | { ok: false; failure: PlantAssetSaveValidationFailure } {
  const { form, assetHierarchy, assetTypes, validUoms, getGatewaySerialValidationMessage } = input;

  const missingFields = collectMissingAssetFields(form);
  if (missingFields.length > 0) {
    return { ok: false, failure: { message: buildMissingFieldsErrorMessage(missingFields) } };
  }

  const simNumberValidationError = form.simNumber.trim() ? validateSimNumber(form.simNumber) : '';
  if (simNumberValidationError) {
    return {
      ok: false,
      failure: { message: simNumberValidationError, simNumberError: simNumberValidationError },
    };
  }

  if (!form.selectedAsset.trim()) {
    return { ok: false, failure: { message: 'Asset type must be selected' } };
  }

  if (isDuplicateAssetName(assetHierarchy, form.assetName, form.selectedAssetId)) {
    return { ok: false, failure: { message: 'Duplicate asset name is not allowed' } };
  }

  const gatewaySerialValidationMessage = getGatewaySerialValidationMessage(form.gatewayDeviceId);
  if (gatewaySerialValidationMessage) {
    return {
      ok: false,
      failure: {
        message: gatewaySerialValidationMessage,
        gatewaySerialError: gatewaySerialValidationMessage,
      },
    };
  }

  const plantId = getStoredPlantId();
  if (!plantId) {
    return { ok: false, failure: { message: 'Plant ID not found. Please create/select a plant first.' } };
  }

  const selectedAssetType = assetTypes.find((type) => type.typeName === form.selectedAsset);
  if (!selectedAssetType) {
    return { ok: false, failure: { message: 'Selected asset type not found.' } };
  }

  const customProbeValidation = validateCustomProbeUoms(
    selectedAssetType,
    form.parameters,
    validUoms
  );
  if (!customProbeValidation.isValid) {
    return {
      ok: false,
      failure: {
        message: `Invalid custom probe configuration: ${customProbeValidation.invalidSensors.join(', ')}`,
      },
    };
  }

  return {
    ok: true,
    data: {
      plantId,
      selectedAssetType,
      mappedParameters: mapParametersForPlantAssetSave(form.parameters),
    },
  };
}

export function buildPlantAssetSavePayload(
  plantId: string,
  form: PlantAssetSaveFormState,
  mappedParameters: MappedAssetParameter[]
) {
  return {
    PlantId: plantId,
    ...(form.selectedAssetId && { AssetId: form.selectedAssetId }),
    Type: form.selectedAsset,
    Name: form.assetName,
    Status: form.assetStatus,
    GatewayDeviceType: form.gatewayDeviceType,
    GatewayDeviceId: form.gatewayDeviceId,
    SimNumber: form.simNumber,
    ControllerType: form.controllerType,
    ControllerDeviceId: form.controllerDeviceId,
    Parameters: mappedParameters,
  };
}

export function buildAssetDetailsFromSave(
  form: PlantAssetSaveFormState,
  mappedParameters: MappedAssetParameter[],
  responseAssetId?: string
): AssetDetails {
  return {
    id: form.selectedAssetId || responseAssetId || crypto.randomUUID(),
    type: form.selectedAsset,
    name: form.assetName,
    status: form.assetStatus,
    parameters: mappedParameters,
    gatewayDeviceType: form.gatewayDeviceType,
    gatewayDeviceId: form.gatewayDeviceId,
    simNumber: form.simNumber,
    controllerType: form.controllerType,
    controllerDeviceId: form.controllerDeviceId,
  };
}

export function persistPlantAssetToLocalStorage(
  assetData: AssetDetails,
  selectedAssetId: string | null,
  plantData: PlantData | null
): void {
  const storedPlantData = localStorage.getItem('currentPlantData');
  if (storedPlantData) {
    try {
      const parsedPlantData = JSON.parse(storedPlantData);
      if (!parsedPlantData.assets) {
        parsedPlantData.assets = [];
      }

      if (selectedAssetId) {
        const assetIndex = parsedPlantData.assets.findIndex(
          (asset: { id: string }) => asset.id === selectedAssetId
        );
        if (assetIndex !== -1) {
          parsedPlantData.assets[assetIndex] = assetData;
        }
      } else {
        parsedPlantData.assets.push(assetData);
      }

      localStorage.setItem('currentPlantData', JSON.stringify(parsedPlantData));
    } catch (parseError) {
      console.error('Error updating localStorage:', parseError);
    }
    return;
  }

  try {
    const newPlantData = {
      customerId: plantData?.customerId || '',
      customerName: plantData?.customerName || '',
      siteId: plantData?.siteId || '',
      siteName: plantData?.siteName || '',
      plantName: plantData?.plantName || '',
      plantStatus: plantData?.plantStatus || 'Active',
      assets: [assetData],
    };
    localStorage.setItem('currentPlantData', JSON.stringify(newPlantData));
  } catch (error) {
    console.error('Error creating localStorage plant data:', error);
  }
}

export function shouldResetOnlyAssetNameAfterSave(
  firstAssetType: AssetType | undefined,
  selectedAsset: string
): boolean {
  return firstAssetType?.typeName === selectedAsset;
}

export function extractPlantAssetSaveErrorMessage(error: unknown): string {
  const err = error as { response?: { data?: { Message?: string } }; message?: string };
  return err?.response?.data?.Message || err?.message || 'Failed to save asset. Please retry.';
}

export async function submitPlantAssetSave(
  form: PlantAssetSaveFormState,
  validation: PlantAssetSaveValidationSuccess
) {
  const payload = buildPlantAssetSavePayload(
    validation.plantId,
    form,
    validation.mappedParameters
  );
  return createPlantAsset(payload);
}

export type PostNewAssetSaveReset = {
  resetOnlyName: boolean;
  firstAssetType?: AssetType;
};

export function getPostNewAssetSaveReset(
  assetTypes: AssetType[],
  selectedAsset: string
): PostNewAssetSaveReset {
  const firstAssetType = assetTypes.length > 0 ? assetTypes[0] : undefined;
  return {
    resetOnlyName: shouldResetOnlyAssetNameAfterSave(firstAssetType, selectedAsset),
    firstAssetType,
  };
}

export function buildOriginalAssetSnapshot(
  form: PlantAssetSaveFormState
): PlantAssetSaveFormState {
  return {
    ...form,
    parameters: form.parameters.map((param) => ({ ...param })),
  };
}
